<script setup lang="ts">
/**
 * AppHeaderMegaMenu — menu « Plateforme » de l'en-tête sur ordinateur, façon
 * Shopify (Karim 2026-11-03) : petites sections aux titres bleus en
 * majuscules, icônes Hugeicons au trait fin (app/assets/icons/menu-*.svg),
 * libellés en gras moyen, colonne « Accompagnement » à droite, ligne d'aide
 * en bas. Fond blanc, celui de notre en-tête.
 *
 * Remplace AppHeaderDropdownPanel (icônes animées d'AppHeaderNavIcon), GARDÉ
 * tel quel avec ses icônes : pour revenir à l'ancien menu, remettre
 * <AppHeaderDropdownPanel> à la place de <AppHeaderMegaMenu> dans
 * AppHeader.vue. Mêmes props, même événement.
 *
 * Les liens viennent de `megaMenuColonnes` / `megaMenuAide` (utils/navLinks).
 *
 * Le même panneau affiche aussi le menu « Choisir Donoor »
 * (AppHeaderMenuChoisir, Karim 2026-11-04) : on passe de l'un à l'autre en
 * survolant les deux boutons, la hauteur suit en douceur.
 */
import { megaMenuAide, megaMenuColonnes } from "~/utils/navLinks";

const { t } = useI18n();

const props = defineProps<{
  activeDropdown: string | null;
  hasOpenDropdown: boolean;
  disableScrollHide: boolean;
}>();

const emit = defineEmits<{
  "nav-click": [item: { to: string; target?: string }];
  /** Fermer le panneau sans changer de page (l'assistant ouvre le chat). */
  fermer: [];
}>();

const route = useRoute();

/** Les deux menus de l'en-tête qui s'ouvrent dans ce panneau. */
type MenuPanneau = "Choisir" | "Plateforme";

const menuOuvert = computed<MenuPanneau | null>(() =>
  props.activeDropdown === "Choisir" || props.activeDropdown === "Plateforme"
    ? props.activeDropdown
    : null,
);

// Le contenu reste affiché pendant que le panneau se referme (Karim
// 2026-11-03, « ça se ferme d'un coup ») : retiré tout de suite, il ne
// laissait rien à replier et la hauteur tombait à zéro sans animation. On ne
// le retire qu'à la fin du repli ; s'il se rouvre avant, rien n'est retiré.
const menuAffiche = ref<MenuPanneau | null>(menuOuvert.value);
watch(menuOuvert, (menu) => {
  if (menu) menuAffiche.value = menu;
});

// Hauteur MESURÉE du contenu (comme BoiteHauteurAnimee) : elle suit
// l'ouverture, le repli et le passage d'un menu à l'autre, qui n'ont pas la
// même hauteur. Avec une grille 0fr → 1fr, ce passage sautait d'un coup.
const contenu = ref<HTMLElement | null>(null);
const hauteur = ref<number | null>(null);
let observateur: ResizeObserver | null = null;

onMounted(() => {
  if (!contenu.value || typeof ResizeObserver === "undefined") return;
  observateur = new ResizeObserver(() => {
    hauteur.value = contenu.value?.offsetHeight ?? 0;
  });
  observateur.observe(contenu.value);
});

onBeforeUnmount(() => observateur?.disconnect());

const styleHauteur = computed(() => {
  if (!props.hasOpenDropdown) return { height: "0px" };
  // Navigateur sans ResizeObserver : hauteur naturelle, sans animation.
  return { height: hauteur.value === null ? "auto" : `${hauteur.value}px` };
});

function finDuRepli(evenement: TransitionEvent) {
  // Les transitions des liens et des lueurs remontent jusqu'ici : seule
  // compte celle de la hauteur du panneau lui-même.
  if (evenement.target !== evenement.currentTarget) return;
  if (evenement.propertyName !== "height") return;
  if (!props.hasOpenDropdown) menuAffiche.value = null;
}

function estActif(to: string) {
  return route.path === to;
}

function cliquer(lien: { to: string; target?: string }) {
  emit("nav-click", lien);
}

// La mascotte de la carte « Accompagnement » sautille tant que la souris est
// sur la colonne.
const mascotteSaute = ref(false);
</script>

<template>
  <!-- La hauteur passe de 0 à celle du contenu, à l'intérieur de la barre
       blanche (effet de morphing). Courbe propre à chaque sens : l'ouverture
       part vite et se pose ; le repli, avec la même courbe, faisait plus de
       80 % du chemin en 90 ms (« d'un coup ») : il accélère donc puis
       ralentit, en douceur. -->
  <div
    class="hidden md:block overflow-hidden transition-[height] duration-300"
    :class="[
      hasOpenDropdown ? 'ease-[cubic-bezier(0.22,1,0.36,1)]' : 'ease-[cubic-bezier(0.4,0,0.2,1)]',
      disableScrollHide ? 'absolute top-full left-0 right-0 bg-white rounded-b-[32px] shadow-lg shadow-black/[0.08]' : '',
    ]"
    :style="styleHauteur"
    @transitionend="finDuRepli"
  >
    <!-- flow-root : les marges du contenu restent dans la mesure. -->
    <div ref="contenu" class="flow-root">
      <AppHeaderMenuChoisir
        v-if="menuAffiche === 'Choisir'"
        class="mega-panneau"
        :class="{ 'mega-panneau--repli': !hasOpenDropdown }"
        @nav-click="cliquer"
        @fermer="emit('fermer')"
      />
      <div
        v-else-if="menuAffiche === 'Plateforme'"
        class="mega-panneau px-8 pt-4 pb-6"
        :class="{ 'mega-panneau--repli': !hasOpenDropdown }"
      >
        <div class="h-px bg-gray-200 mb-7" />

        <!-- Trois colonnes de sections + colonne « Accompagnement » (xl+ :
             en dessous, les trois colonnes seraient trop étroites). -->
        <div class="grid grid-cols-3 gap-x-8 xl:grid-cols-[repeat(3,minmax(0,1fr))_300px]">
          <div
            v-for="(colonne, indexColonne) in megaMenuColonnes"
            :key="indexColonne"
            class="mega-colonne flex flex-col gap-[38px]"
            :style="{ '--rang': indexColonne }"
          >
            <section v-for="section in colonne" :key="section.titreCle">
              <p class="mega-titre mb-[14px]">{{ t(section.titreCle) }}</p>
              <ul class="flex flex-col">
                <li v-for="lien in section.liens" :key="lien.to">
                  <NuxtLink
                    :to="lien.to"
                    class="mega-lien"
                    :aria-current="estActif(lien.to) ? 'page' : undefined"
                    @click.prevent="cliquer(lien)"
                  >
                    <UIcon :name="lien.icon" class="mega-icone" />
                    <span class="mega-libelle">{{ t(lien.labelCle) }}</span>
                    <svg class="mega-fleche" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>
                  </NuxtLink>
                </li>
              </ul>
            </section>
          </div>

          <!-- Colonne de droite, construite comme celle de Shopify (Karim
               2026-11-03) : titre de section, carte visuelle, puis titre et
               texte sur le fond grisé, et enfin les deux canaux de contact
               de l'ancien menu (démo Calendly, WhatsApp), calés en bas.
               La carte montre notre mascotte, qui sautille au survol. -->
          <div
            class="mega-colonne mega-accompagnement hidden xl:flex flex-col rounded-3xl bg-gray-50 p-5"
            :style="{ '--rang': 3 }"
            @mouseenter="mascotteSaute = true"
            @mouseleave="mascotteSaute = false"
          >
            <p class="mega-titre mb-[14px]">{{ t("navigation.appHeader.megaMenu.accompagnement") }}</p>

            <div class="relative isolate grid h-[150px] place-items-center overflow-hidden rounded-2xl bg-black">
              <div class="mega-lueur mega-lueur--haut" aria-hidden="true" />
              <div class="mega-lueur mega-lueur--bas" aria-hidden="true" />
              <div class="size-[68px]" aria-hidden="true">
                <DonoorMascotte :saute="mascotteSaute" />
              </div>
            </div>

            <p class="mt-4 text-[16px] font-medium leading-6 text-[#0f0f0f]">
              {{ t("navigation.appHeader.dropdown.commencerTitre") }}
            </p>
            <p class="mt-1 text-[15px] leading-snug text-gray-500">
              {{ t("navigation.appHeader.dropdown.commencerTexte") }}
            </p>

            <div class="mt-auto flex flex-col gap-2.5 pt-5">
              <CalendlyButton
                :label="t('navigation.appHeader.reserverDemo')"
                class="w-full px-5 py-2.5 text-[14px]"
              />
              <WhatsAppButton
                :label="t('navigation.appHeader.nousContacter')"
                class="w-full px-5 py-2.5 text-[14px]"
              />
            </div>
          </div>
        </div>

        <!-- Ligne du bas, sur toute la largeur : titre puis trois liens avec
             leur description, sans icône (comme la ligne « Personnaliser et
             développer » de Shopify). -->
        <div
          class="mega-colonne -mx-8 mt-7 border-t border-gray-200 px-8 pt-5"
          :style="{ '--rang': 4 }"
        >
          <div class="grid grid-cols-3 gap-x-8 xl:grid-cols-[repeat(3,minmax(0,1fr))_300px]">
            <p class="mega-titre col-span-3 mb-3 xl:col-span-1 xl:mb-0 xl:pt-0.5">
              {{ t(megaMenuAide.titreCle) }}
            </p>
            <NuxtLink
              v-for="lien in megaMenuAide.liens"
              :key="lien.to"
              :to="lien.to"
              class="mega-lien-aide"
              :aria-current="estActif(lien.to) ? 'page' : undefined"
              @click.prevent="cliquer(lien)"
            >
              <span class="flex items-center gap-3">
                <span class="mega-libelle">{{ t(lien.labelCle) }}</span>
                <svg class="mega-fleche" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>
              </span>
              <span class="mega-description block text-[15px] leading-snug">
                {{ t(lien.descriptionCle) }}
              </span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Titres de section : majuscules espacées, bleu de marque. Mesurés sur le
   menu de Shopify (Karim 2026-11-03) : 13 px, espacement 0,08 em ; semi-gras
   pour garder sur fond blanc le poids qu'ils ont sur le fond noir de Shopify. */
/* Pas de marge ici : un style de ce bloc l'emporterait sur les classes
   Tailwind (mb-3, xl:mb-0) posées dans le gabarit. */
.mega-titre {
  font-size: 13px;
  font-weight: 600;
  line-height: 1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-main);
}

/* Couleur portée par le lien : libellé, icône et flèche la suivent
   (currentColor), ce qui permet d'éteindre tout le lien d'un coup. */
.mega-lien {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-block: 0.5rem;
  border-radius: 0.5rem;
  color: #0f0f0f;
  transition: color 200ms ease;
}

.mega-lien-aide {
  display: block;
  border-radius: 0.5rem;
  color: #0f0f0f;
  transition: color 200ms ease;
}

.mega-description {
  color: #6b7280;
  transition: color 200ms ease;
}

/* 16 px en gras moyen (500), presque noir. 600 faisait trop gras (Karim
   2026-11-03) ; la police n'est chargée qu'en 400/500/600/700, pas de
   graisse intermédiaire possible. */
.mega-libelle {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5rem;
}

/* Icônes Hugeicons (trait de 1,5 sur 24, soit 1,25 px à 20 px), la taille
   des icônes de Shopify. */
.mega-icone {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

/* Flèche cachée, révélée au survol de son lien. La marge négative ramène
   l'écart libellé → flèche à 12 px (16 px de `gap` sur la ligne). */
.mega-fleche {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-left: -4px;
  opacity: 0;
  transform: translateX(-6px);
  transition:
    opacity 200ms ease,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.mega-lien-aide .mega-fleche {
  margin-left: 0;
}

/* Survol comme Shopify (Karim 2026-11-03) : le lien survolé reste noir et
   sa flèche apparaît ; TOUS les autres liens du menu s'éteignent en gris.
   Aucune couleur de marque ici. Le clavier (focus-visible) a le même effet. */
.mega-lien:is(:hover, :focus-visible) .mega-fleche,
.mega-lien-aide:is(:hover, :focus-visible) .mega-fleche {
  opacity: 1;
  transform: none;
}
.mega-panneau:has(:is(.mega-lien, .mega-lien-aide):is(:hover, :focus-visible))
  :is(.mega-lien, .mega-lien-aide):not(:hover, :focus-visible) {
  color: #a3a3a3;
}
.mega-panneau:has(:is(.mega-lien, .mega-lien-aide):is(:hover, :focus-visible))
  .mega-lien-aide:not(:hover, :focus-visible)
  .mega-description {
  color: #d4d4d4;
}

/* Carte visuelle de la colonne « Accompagnement » : fond noir, deux lueurs
   bleues de marque qui se rapprochent légèrement du centre au survol de la
   colonne. */
.mega-lueur {
  position: absolute;
  z-index: -1;
  width: 180px;
  height: 180px;
  border-radius: 9999px;
  background: var(--color-main);
  filter: blur(48px);
  pointer-events: none;
  transition:
    transform 600ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 600ms ease;
}
.mega-lueur--haut {
  top: -90px;
  right: -60px;
  opacity: 0.6;
}
.mega-lueur--bas {
  bottom: -110px;
  left: -70px;
  opacity: 0.35;
}
.mega-accompagnement:hover .mega-lueur--haut {
  transform: translate(-16px, 14px);
  opacity: 0.75;
}
.mega-accompagnement:hover .mega-lueur--bas {
  transform: translate(16px, -14px);
  opacity: 0.5;
}

/* Ouverture : les colonnes apparaissent l'une après l'autre (40 ms d'écart),
   en montant de quelques pixels. Une animation et pas une transition : le
   contenu est créé à l'ouverture (v-if), une transition n'aurait pas d'état
   de départ. L'animation est donc rejouée à chaque ouverture. */
.mega-colonne {
  animation: mega-entree 380ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(var(--rang, 0) * 40ms);
}

@keyframes mega-entree {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* Repli : le contenu s'efface pendant que la hauteur se replie, au lieu
   d'être simplement rogné par le bas de la barre. S'il se rouvre en cours de
   route, il revient en fondu. */
.mega-panneau {
  transition: opacity 200ms ease;
}
.mega-panneau--repli {
  opacity: 0;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .mega-colonne {
    animation: none;
  }
  .mega-panneau {
    transition: none;
  }
  .mega-lien,
  .mega-lien-aide,
  .mega-description,
  .mega-fleche,
  .mega-lueur {
    transition: none;
  }
}
</style>
